import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useSessionStorage } from '@vueuse/core';
import type {
    BffCompanyConsentResponse,
    BffCompanyPolicyResponse,
    BffGdprTextsResponse,
    BffGeneralPolicyResponse,
} from '../types/types';

export const defaultGeneralPolicy = (): BffGeneralPolicyResponse & { exists: boolean } => ({
    legalTextExternalId: null,
    body: '',
    url: null,
    exists: false,
});

export const defaultCompanyPolicy = (): BffCompanyPolicyResponse & { exists: boolean } => ({
    legalTextExternalId: null,
    body: '',
    url: null,
    exists: false,
});

export const defaultCompanyConsent = (): BffCompanyConsentResponse & { exists: boolean } => ({
    legalTextExternalId: null,
    body: '',
    url: null,
    mandatory: 0, // '0' => not mandatory, '1' => mandatory
    exists: false,
});

export const useGdprTextsStore = defineStore('gdpr-texts', () => {
    const generalPolicy = useSessionStorage('lokarb:gdpr:generalPolicy', defaultGeneralPolicy());
    const companyPolicy = useSessionStorage('lokarb:gdpr:companyPolicy', defaultCompanyPolicy());
    const companyConsent = useSessionStorage('lokarb:gdpr:companyConsent', defaultCompanyConsent());

    // GDPR Consent
    const generalPolicyChecked = ref(false);
    const companyPolicyChecked = ref(false);
    const companyConsentChecked = ref(false);

    const fetched = computed(() => generalPolicy.value.legalTextExternalId !== null);
    const generalPolicyBody = computed(() => generalPolicy.value.body);
    const companyPolicyBody = computed(() => companyPolicy.value.body);
    const companyConsentBody = computed(() => companyConsent.value.body);

    const generalPolicyUrl = computed(() => generalPolicy.value.url);
    const companyPolicyUrl = computed(() => companyPolicy.value.url);
    const companyConsentUrl = computed(() => companyConsent.value.url);
    const companyConsentMandatory = computed(() => companyConsent.value.mandatory === 1);

    function reset() {
        generalPolicy.value = defaultGeneralPolicy();
        companyPolicy.value = defaultCompanyPolicy();
        companyConsent.value = defaultCompanyConsent();

        generalPolicyChecked.value = false;
        companyPolicyChecked.value = false;
        companyConsentChecked.value = false;
    }

    function setTexts(gdprTexts: Partial<BffGdprTextsResponse>) {
        reset();
        generalPolicy.value = { ...gdprTexts.generalPolicy, exists: true };

        if (gdprTexts.companyPolicy) {
            companyPolicy.value = { ...gdprTexts.companyPolicy, exists: true };
        } else {
            companyPolicy.value = defaultCompanyPolicy();
        }

        if (gdprTexts.companyConsent) {
            companyConsent.value = { ...gdprTexts.companyConsent, exists: true };
        } else {
            companyConsent.value = defaultCompanyConsent();
        }
    }

    return {
        // State
        generalPolicy,
        companyPolicy,
        companyConsent,
        generalPolicyChecked,
        companyPolicyChecked,
        companyConsentChecked,

        // Getters
        fetched,
        generalPolicyBody,
        companyPolicyBody,
        companyConsentBody,
        generalPolicyUrl,
        companyPolicyUrl,
        companyConsentUrl,
        companyConsentMandatory,

        // Actions
        $reset: reset,
        setTexts,
    };
});
